import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useState } from 'react';

import {
  DATASET_CREATE_DATA_TYPES,
  MEETING_DATA_TYPE_LABEL,
  MEETING_ENTRY_FIELDS,
} from '../../../constants/index.js';
import type { MeetingEntryFieldKey } from '../../../constants/index.js';
import type {
  CreateDataEntryRequest,
  DataEntry,
  DatasetCreateDataType,
} from '../../../services/dataHubService.interface.js';

import { useStyles } from './AddDataEntryDialog.style.js';

export interface AddDataEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CreateDataEntryRequest) => Promise<DataEntry>;
  onCreated?: (entry: DataEntry) => void;
}

const PROPERTIES_PLACEHOLDER = '{}';

const EMPTY_MEETING_FIELDS: Record<MeetingEntryFieldKey, string> = {
  meetingTitle: '',
  language: '',
  meetingDate: '',
  organizerId: '',
};

export function AddDataEntryDialog({
  open,
  onClose,
  onSubmit,
  onCreated,
}: AddDataEntryDialogProps) {
  const styles = useStyles();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState<DatasetCreateDataType>('Generic');
  const [meetingFields, setMeetingFields] =
    useState<Record<MeetingEntryFieldKey, string>>(EMPTY_MEETING_FIELDS);
  const [propertiesText, setPropertiesText] = useState('');
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  const nameError =
    nameTouched && name.trim().length === 0 ? 'Name is required.' : null;
  const isValid = name.trim().length > 0 && propertiesError === null;

  const validateProperties = useCallback((text: string) => {
    const trimmed = text.trim();
    if (trimmed === '' || trimmed === '{}') {
      setPropertiesError(null);
      return true;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        typeof parsed !== 'object' ||
        Array.isArray(parsed) ||
        parsed === null
      ) {
        setPropertiesError('Must be a JSON object.');
        return false;
      }
      setPropertiesError(null);
      return true;
    } catch {
      setPropertiesError('Invalid JSON.');
      return false;
    }
  }, []);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setDataType('Generic');
    setMeetingFields(EMPTY_MEETING_FIELDS);
    setPropertiesText('');
    setPropertiesError(null);
    setSubmitError(null);
    setNameTouched(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const setMeetingField = useCallback(
    (key: MeetingEntryFieldKey, value: string) => {
      setMeetingFields((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;

    const trimmedProps = propertiesText.trim();
    let properties: Record<string, unknown> | undefined;
    if (trimmedProps !== '' && trimmedProps !== '{}') {
      try {
        properties = JSON.parse(trimmedProps) as Record<string, unknown>;
      } catch {
        setPropertiesError('Invalid JSON.');
        return;
      }
    }

    // Meeting-specific scalar fields are sent as top-level request properties,
    // only when the Meeting type is selected and the field was filled in.
    const meetingValues: Partial<Record<MeetingEntryFieldKey, string>> = {};
    if (dataType === MEETING_DATA_TYPE_LABEL) {
      for (const { key } of MEETING_ENTRY_FIELDS) {
        const value = meetingFields[key].trim();
        if (value !== '') meetingValues[key] = value;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const entry = await onSubmit({
        name: name.trim(),
        dataType,
        description: description.trim() !== '' ? description.trim() : undefined,
        properties,
        ...meetingValues,
      });
      onCreated?.(entry);
      resetForm();
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create entry.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isValid,
    name,
    dataType,
    description,
    meetingFields,
    propertiesText,
    onSubmit,
    onCreated,
    resetForm,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add Data Entry</DialogTitle>

          <DialogContent>
            <Field
              label="Name"
              required
              validationState={nameError !== null ? 'error' : 'none'}
              validationMessage={nameError ?? undefined}
            >
              <Input
                placeholder="Entry name"
                value={name}
                onChange={(_e, d) => setName(d.value)}
                onBlur={() => setNameTouched(true)}
                disabled={isSubmitting}
              />
            </Field>

            <Field label="Description">
              <Input
                placeholder="Optional description"
                value={description}
                onChange={(_e, d) => setDescription(d.value)}
                disabled={isSubmitting}
              />
            </Field>

            <Field label="Data Type" required>
              <Select
                value={dataType}
                onChange={(_e, d) =>
                  setDataType(d.value as DatasetCreateDataType)
                }
                disabled={isSubmitting}
              >
                {DATASET_CREATE_DATA_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Field>

            {dataType === MEETING_DATA_TYPE_LABEL &&
              MEETING_ENTRY_FIELDS.map((field) => (
                <Field key={field.key} label={field.label}>
                  <Input
                    type={field.isDate === true ? 'date' : 'text'}
                    placeholder={field.placeholder}
                    value={meetingFields[field.key]}
                    onChange={(_e, d) => setMeetingField(field.key, d.value)}
                    disabled={isSubmitting}
                  />
                </Field>
              ))}

            <Field
              label="Extra Properties (JSON)"
              validationState={propertiesError !== null ? 'error' : 'none'}
              validationMessage={propertiesError ?? undefined}
            >
              <Textarea
                placeholder={PROPERTIES_PLACEHOLDER}
                value={propertiesText}
                onChange={(_e, d) => {
                  setPropertiesText(d.value);
                  validateProperties(d.value);
                }}
                disabled={isSubmitting}
                rows={5}
              />
            </Field>

            {submitError !== null && (
              <div role="alert" className={styles.submitError}>
                {submitError}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              appearance="primary"
              disabled={!isValid || isSubmitting}
              onClick={() => {
                void handleSubmit();
              }}
              icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
